import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { getPublicName } from './UserProfile';
import UserAvatar from './UserAvatar';
import { findOrStartGearConversation, findOrStartLocalExpertConversation, LOCAL_EXPERT_TOPIC } from './messagingUtils';
import { splitPublicAndPrivateProfile } from './userPrivateProfile';
import { useToast } from './Toast';

const profileCache = new Map();

export function clearProfileCache(userId) {
  if (userId) profileCache.delete(userId);
  else profileCache.clear();
}

export async function fetchUserProfile(userId) {
  if (!userId) return null;
  if (profileCache.has(userId)) return profileCache.get(userId);

  try {
    const snap = await getDoc(doc(db, 'users', userId));
    const rawData = snap.exists() ? { id: userId, ...snap.data() } : { id: userId };
    const { publicData } = splitPublicAndPrivateProfile(rawData);
    const data = { id: userId, ...publicData };
    profileCache.set(userId, data);
    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { id: userId };
  }
}

export function getCachedPublicName(userId, fallback = 'Member') {
  const profile = profileCache.get(userId);
  return getPublicName(profile, fallback);
}

export function ProfileLink({
  userId,
  size = 'md',
  showAvatar = true,
  label,
  style,
  nameStyle,
}) {
  const [profile, setProfile] = useState(profileCache.get(userId) || null);

  useEffect(() => {
    if (!userId) return undefined;

    let mounted = true;
    fetchUserProfile(userId).then((data) => {
      if (mounted) setProfile(data);
    });

    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!userId) return null;

  const name = getPublicName(profile, 'Member');
  const avatarSize = size === 'sm' ? 28 : size === 'lg' ? 48 : 36;
  const fontSize = size === 'sm' ? '0.9rem' : size === 'lg' ? '1.05rem' : '0.95rem';

  return (
    <Link
      to={`/profile/${userId}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        textDecoration: 'none',
        color: '#3b82f6',
        fontWeight: '600',
        fontSize,
        ...style,
      }}
    >
      {showAvatar && (
        <UserAvatar
          profile={profile}
          userId={userId}
          name={name}
          size={avatarSize}
        />
      )}
      <span style={nameStyle}>
        {label ? `${label} ` : ''}
        {name}
      </span>
    </Link>
  );
}

export function ProfileRow({ label, userId, size = 'sm' }) {
  if (!userId) return null;

  return (
    <p
      style={{
        margin: '8px 0',
        color: '#4b5563',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
      }}
    >
      <strong>{label}:</strong>
      <ProfileLink userId={userId} size={size} showAvatar />
    </p>
  );
}

function formatMemberSince(createdAt) {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function OwnerStoryCard({ userId, user, gearId, gearName, listingNote = '' }) {
  const [profile, setProfile] = useState(profileCache.get(userId) || null);

  useEffect(() => {
    if (!userId) return undefined;

    let mounted = true;
    fetchUserProfile(userId).then((data) => {
      if (mounted) setProfile(data);
    });

    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!userId) return null;

  const name = getPublicName(profile, 'Member');
  const location = profile?.location?.trim?.() || profile?.location || '';
  const listingStory = listingNote?.trim() || '';
  const about = profile?.aboutMe?.trim() || '';
  const memberSince = formatMemberSince(profile?.createdAt);
  const storyText = listingStory
    || about
    || `${name} has not added a bio yet. Send a message if you want to know more about this gear or pickup.`;

  return (
    <section className="owner-story-card" aria-label={`About ${name}`}>
      <h2 className="owner-story-title">From the owner</h2>
      <div className="owner-story-inner">
        <div className="owner-story-header">
          <ProfileLink userId={userId} size="lg" showAvatar />
          <div className="owner-story-meta">
            {profile?.localExpert && (
              <span className="owner-story-expert-badge">Local Expert</span>
            )}
            {location && <p>Based in {location}</p>}
            {memberSince && <p>Member since {memberSince}</p>}
          </div>
        </div>
        <p className="owner-story-body">{storyText}</p>
        <div className="owner-story-actions">
          <Link to={`/profile/${userId}`} className="owner-story-profile-link">
            View profile
          </Link>
          <MessageUserButton
            user={user}
            recipientId={userId}
            gearId={gearId}
            gearName={gearName}
            label="Message"
          />
        </div>
      </div>
    </section>
  );
}

export function HostCard({ userId, compact = false }) {
  const [profile, setProfile] = useState(profileCache.get(userId) || null);

  useEffect(() => {
    if (!userId) return undefined;

    let mounted = true;
    fetchUserProfile(userId).then((data) => {
      if (mounted) setProfile(data);
    });

    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!userId) return null;

  const location = profile?.location?.trim?.() || profile?.location || '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: compact ? 'center' : 'flex-start',
        gap: '12px',
        padding: compact ? '10px 14px' : '14px 16px',
        marginBottom: compact ? '12px' : '16px',
        borderRadius: '12px',
        backgroundColor: '#f0f9ff',
        border: '1px solid #bfdbfe',
      }}
    >
      <ProfileLink userId={userId} size={compact ? 'sm' : 'md'} label="Hosted by" />
      {!compact && location && (
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
          {location}
        </span>
      )}
    </div>
  );
}

export function ReviewAttribution({ reviewerId, reviewerName }) {
  if (reviewerId) {
    return <ProfileLink userId={reviewerId} size="sm" showAvatar={false} />;
  }

  return <span>{getPublicName(reviewerName, reviewerName || 'Anonymous')}</span>;
}

export function ProfileChip({ userId, label }) {
  const [profile, setProfile] = useState(profileCache.get(userId) || null);

  useEffect(() => {
    if (!userId) return undefined;

    let mounted = true;
    fetchUserProfile(userId).then((data) => {
      if (mounted) setProfile(data);
    });

    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!userId) return null;

  const name = getPublicName(profile, 'Member');

  return (
    <Link
      to={`/profile/${userId}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px 4px 4px',
        borderRadius: '9999px',
        backgroundColor: '#f3f4f6',
        border: '1px solid #e5e7eb',
        textDecoration: 'none',
        color: '#374151',
        fontWeight: '600',
        fontSize: '0.85rem',
      }}
    >
      <UserAvatar
        profile={profile}
        userId={userId}
        name={name}
        size={24}
      />
      <span>
        {label ? `${label} ` : ''}
        {name}
      </span>
    </Link>
  );
}

export function MessageUserButton({
  user,
  recipientId,
  gearId,
  gearName,
  label = 'Message',
  initialMessage,
  compact = false,
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  if (!recipientId || (user && user.uid === recipientId)) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      showToast('Please log in to send a message.', 'info');
      return;
    }

    setLoading(true);
    try {
      const conversationId = await findOrStartGearConversation({
        currentUserId: user.uid,
        recipientId,
        gearId,
        gearName,
        initialMessage,
      });
      navigate('/messaging', {
        state: {
          openConversationId: conversationId,
          pendingConversation: {
            id: conversationId,
            gearId,
            gearName: gearName || 'Gear',
            otherUserId: recipientId,
          },
        },
      });
    } catch (error) {
      showToast(error.message || 'Could not start conversation.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      style={{
        padding: compact ? '4px 10px' : '6px 14px',
        backgroundColor: loading ? '#9ca3af' : '#ffffff',
        color: '#3b82f6',
        border: '1px solid #bfdbfe',
        borderRadius: '9999px',
        fontWeight: '600',
        fontSize: compact ? '0.8rem' : '0.85rem',
        cursor: loading ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {loading ? 'Opening...' : label}
    </button>
  );
}

export function MessageLocalExpertButton({
  user,
  expertId,
  expertLocation,
  label = 'Ask about the area',
  subtle = false,
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  if (!expertId || (user && user.uid === expertId)) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user) {
      showToast('Please log in to message a local expert.', 'info');
      return;
    }

    setLoading(true);
    try {
      const conversationId = await findOrStartLocalExpertConversation({
        currentUserId: user.uid,
        expertId,
        expertLocation,
      });
      navigate('/messaging', {
        state: {
          openConversationId: conversationId,
          pendingConversation: {
            id: conversationId,
            conversationType: 'local-expert',
            gearName: LOCAL_EXPERT_TOPIC,
            otherUserId: expertId,
          },
        },
      });
    } catch (error) {
      showToast(error.message || 'Could not start conversation.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buttonStyle = subtle
    ? {
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: '12px',
        padding: '8px 14px',
        backgroundColor: '#ffffff',
        color: '#047857',
        border: '1px solid #a7f3d0',
        borderRadius: '9999px',
        fontWeight: '600',
        fontSize: '0.88rem',
        cursor: loading ? 'not-allowed' : 'pointer',
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '12px',
        marginBottom: '8px',
        padding: '12px 20px',
        backgroundColor: loading ? '#6ee7b7' : '#059669',
        color: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        fontWeight: '600',
        fontSize: '0.95rem',
        cursor: loading ? 'not-allowed' : 'pointer',
      };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      style={buttonStyle}
    >
      {!subtle && <span aria-hidden="true">🗺️</span>}
      {loading ? 'Opening chat...' : label}
    </button>
  );
}